import { useMemo, useState } from 'react';
import { Calendar, MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Modal } from '~/components/overlays/Modal';
import { ModalHeader } from '~/components/overlays/ModalHeader';
import { Button, Input, StepIndicator } from '~/components/ui';
import { CurrencySelector } from '~/components/ui/inputs/CurrencySelector';
import type { BundleProductLengthLike } from '~/components/ui/bundleQuantity';
import {
  formatCurrency,
  formatDateOnly,
  sellerLabel,
  getLocalDateString,
  priceUnitLabel,
  toastGraphQLError,
} from '~/utils';
import { contactLabel } from '~/utils/contact';
import {
  SellerContactsSummaryRow,
  type SellerContactOrganization,
} from '~/components/common/SellerContactsSummaryRow';
import { defaultContactIdsByOrg } from '~/hooks/useContactSelection';
import { toggleId } from '~/utils/toggleId';
import { useSubmitOnce } from '~/hooks/useSubmitOnce';
import {
  useOrganizationsContactData,
  organizationContactDataById,
  sellerContactsOf,
} from '~/api';
import { useGetLocations } from '~/api/location';
import { useCreateRequestForQuote } from '~/api/requestForQuote';
import { RequestForQuoteSummaryPanels } from '~/pages/buy-lists/sections/RequestForQuoteSummaryPanels';
import { BundleQuantityLabel } from '~/components/ui/BundleQuantityLabel';
import type { Currency, Quantity, UnitCode } from '~/graphql/graphql';
import { SelectLengthsStep } from './SelectLengthsStep';
import {
  EMPTY_LENGTH_INPUT,
  isPastDate,
  itemsStepIsValid,
  leadTimeDaysUntil,
  parseTargetPrice,
  type LengthInput,
} from './requestQuote';

export type RequestQuoteLength = {
  platformProductLengthId: string;
  label: string;
  defaultQuantity: Quantity;
  platformProductsLength: BundleProductLengthLike;
};

type RequestQuoteModalProps = {
  onClose: () => void;
  onSubmitted: (requestForQuoteId: string) => void;
  productName: string;
  priceUnit: UnitCode;
  lengths: RequestQuoteLength[];
  organizations: SellerContactOrganization[];
  shippingLocationId: string;
  defaultCurrency: Currency;
};

type Step = 'items' | 'contacts' | 'summary';

export function RequestQuoteModal({
  onClose,
  onSubmitted,
  productName,
  priceUnit,
  lengths,
  organizations,
  shippingLocationId,
  defaultCurrency,
}: Readonly<RequestQuoteModalProps>) {
  const { t } = useTranslation('order-builder');
  const [step, setStep] = useState<Step>('items');
  const [selectedLengthIds, setSelectedLengthIds] = useState<Set<string>>(
    () => new Set(lengths.map((length) => length.platformProductLengthId))
  );
  const [inputsByLengthId, setInputsByLengthId] = useState<
    Map<string, LengthInput>
  >(
    () =>
      new Map(
        lengths.map((length) => [
          length.platformProductLengthId,
          {
            ...EMPTY_LENGTH_INPUT,
            quantity:
              length.defaultQuantity.value === 0
                ? ''
                : String(length.defaultQuantity.value),
          },
        ])
      )
  );
  const [touchedLengthIds, setTouchedLengthIds] = useState<Set<string>>(
    () => new Set()
  );
  const [nextAttempted, setNextAttempted] = useState(false);
  const [currency, setCurrency] = useState<Currency>(defaultCurrency);
  const [neededBy, setNeededBy] = useState('');
  const [contactIdsByOrg, setContactIdsByOrg] = useState<Map<string, string[]>>(
    () => new Map()
  );
  const [primaryContactIdByOrg, setPrimaryContactIdByOrg] = useState<
    Map<string, string>
  >(() => new Map());

  const {
    data: locationsData,
    isLoading: locationsLoading,
    isError: locationsError,
  } = useGetLocations();
  const shippingLocation = locationsData?.locations?.find(
    (location) => location.id === shippingLocationId
  );
  const shipToUnavailable =
    !locationsLoading && (locationsError || !shippingLocation);
  const shipToName = shippingLocation?.name ?? '';

  const organizationIds = useMemo(
    () => organizations.map((organization) => organization.id),
    [organizations]
  );
  const { data: orgContactData, isError: isContactDataError } =
    useOrganizationsContactData(organizationIds);
  const contactDataLoaded = orgContactData !== undefined || isContactDataError;
  const contactDataByOrgId = useMemo(
    () => organizationContactDataById(orgContactData?.organizationsByIds),
    [orgContactData]
  );
  const defaultContactIdsByOrgId = useMemo(
    () => defaultContactIdsByOrg(contactDataByOrgId),
    [contactDataByOrgId]
  );
  const selectedContactIdsFor = (organizationId: string) =>
    contactIdsByOrg.get(organizationId) ??
    defaultContactIdsByOrgId.get(organizationId) ??
    [];
  const inputFor = (platformProductLengthId: string) =>
    inputsByLengthId.get(platformProductLengthId) ?? EMPTY_LENGTH_INPUT;

  const { mutate, isPending } = useCreateRequestForQuote();
  const submitOnce = useSubmitOnce();

  const selectedLengths = lengths
    .filter((length) => selectedLengthIds.has(length.platformProductLengthId))
    .map((length) => {
      const input = inputFor(length.platformProductLengthId);
      return {
        length,
        quantity: Number(input.quantity),
        pickupPrice: parseTargetPrice(input.pickupPrice),
        deliveryPrice: parseTargetPrice(input.deliveryPrice),
      };
    });
  const neededByInPast = isPastDate(neededBy);
  const itemsValid = itemsStepIsValid(
    neededBy,
    selectedLengths.map((item) => ({
      quantity: item.quantity,
      pickupPrice: item.pickupPrice,
      deliveryPrice: item.deliveryPrice,
      platformProductsLength: item.length.platformProductsLength,
      unit: item.length.defaultQuantity.unit,
    }))
  );
  const contactsValid =
    contactDataLoaded && !isContactDataError && organizations.length > 0;

  const updateInput = (
    platformProductLengthId: string,
    field: keyof LengthInput,
    value: string
  ) => {
    setInputsByLengthId((previous) => {
      const next = new Map(previous);
      next.set(platformProductLengthId, {
        ...(previous.get(platformProductLengthId) ?? EMPTY_LENGTH_INPUT),
        [field]: value,
      });
      return next;
    });
  };

  const touchLength = (platformProductLengthId: string) => {
    setTouchedLengthIds((previous) =>
      previous.has(platformProductLengthId)
        ? previous
        : new Set(previous).add(platformProductLengthId)
    );
  };

  const handleNext = () => {
    if (step === 'items') {
      if (!itemsValid) {
        setNextAttempted(true);
        return;
      }
      setStep('contacts');
      return;
    }
    if (!contactsValid) return;
    setStep('summary');
  };

  const formatPrice = (amount: number | undefined) =>
    amount === undefined
      ? t('order_builder.request_quote.no_target_price')
      : `${formatCurrency(amount, currency)}/${priceUnitLabel(priceUnit)}`;

  const recipientGroups = organizations.map((organization) => {
    const contacts = sellerContactsOf(contactDataByOrgId, organization.id);
    const selectedIds = new Set(selectedContactIdsFor(organization.id));
    return {
      organizationId: organization.id,
      organizationLabel: sellerLabel(organization),
      contacts: contacts
        .filter((contact) => selectedIds.has(contact.id))
        .map((contact) => ({
          id: contact.id,
          name:
            contact.id === primaryContactIdByOrg.get(organization.id)
              ? t('order_builder.request_quote.primary_contact_name', {
                  name: contactLabel(contact),
                })
              : contactLabel(contact),
        })),
    };
  });

  const updateSellerContacts = (
    organizationId: string,
    contactIds: string[],
    primaryContactId: string | undefined
  ) => {
    setContactIdsByOrg((previous) =>
      new Map(previous).set(organizationId, contactIds)
    );
    setPrimaryContactIdByOrg((previous) => {
      const next = new Map(previous);
      if (primaryContactId === undefined) {
        next.delete(organizationId);
      } else {
        next.set(organizationId, primaryContactId);
      }
      return next;
    });
  };

  const handleSubmit = () => {
    submitOnce((onSettled) => {
      mutate(
        {
          shippingLocationId,
          currency,
          leadTimeDays: leadTimeDaysUntil(neededBy),
          items: selectedLengths.map((item) => ({
            platformProductLengthId: item.length.platformProductLengthId,
            quantity: {
              value: item.quantity,
              unit: item.length.defaultQuantity.unit,
            },
            pickupPrice:
              item.pickupPrice.amount === undefined
                ? undefined
                : { amount: item.pickupPrice.amount, currency },
            deliveryPrice:
              item.deliveryPrice.amount === undefined
                ? undefined
                : { amount: item.deliveryPrice.amount, currency },
          })),
          sellerInvitations: organizations.map((organization) => ({
            sellingOrganizationId: organization.id,
            recipientUserIds: selectedContactIdsFor(organization.id),
            primaryContactUserId: primaryContactIdByOrg.get(organization.id),
          })),
        },
        {
          onSuccess: (data) => onSubmitted(data.createRequestForQuote.id),
          onError: toastGraphQLError,
          onSettled,
        }
      );
    });
  };

  const handleClose = () => {
    if (isPending) return;
    onClose();
  };

  const stepTitles: Record<Step, string> = {
    items: t('order_builder.request_quote.step_items_title'),
    contacts: t('order_builder.request_quote.step_contacts_title'),
    summary: t('order_builder.request_quote.step_summary_title'),
  };

  return (
    <Modal isOpen onClose={handleClose}>
      <div className="flex h-[min(760px,92vh)] w-[min(1024px,94vw)] flex-col overflow-hidden rounded-2xl bg-neut-light shadow-2xl">
        <ModalHeader
          title={stepTitles[step]}
          description={productName}
          onClose={handleClose}
          closeDisabled={isPending}
        />

        <div className="shrink-0 border-y border-neut-border bg-neut-50 px-8 py-4">
          <StepIndicator
            steps={[
              {
                id: 'items',
                label: t('order_builder.request_quote.step_items_label'),
              },
              {
                id: 'contacts',
                label: t('order_builder.request_quote.step_contacts_label'),
              },
              {
                id: 'summary',
                label: t('order_builder.request_quote.step_summary_label'),
              },
            ]}
            currentStepId={step}
            testId="request-quote-step-indicator"
          />
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-8 py-5">
          {step === 'items' && (
            <div className="flex flex-col gap-4">
              <div className="flex flex-wrap items-end gap-4">
                <div className="w-40">
                  <CurrencySelector
                    name="currency"
                    label={t('order_builder.request_quote.currency_label')}
                    value={currency}
                    onChange={(_, value) => setCurrency(value as Currency)}
                  />
                </div>
                <div className="w-48">
                  <Input
                    name="neededBy"
                    type="date"
                    label={t('order_builder.request_quote.needed_by_label')}
                    min={getLocalDateString()}
                    value={neededBy}
                    error={
                      neededByInPast
                        ? t('order_builder.request_quote.needed_by_in_past')
                        : undefined
                    }
                    onChange={(_, value) => setNeededBy(value)}
                  />
                </div>
                <div
                  className="flex flex-col gap-2 text-sm"
                  data-testid="request-quote-ship-to"
                >
                  <span className="font-medium text-neut-prim">
                    {t('order_builder.request_quote.ship_to_label')}
                  </span>
                  <span className="py-3 text-neut-sec">
                    {locationsLoading
                      ? '–'
                      : shipToUnavailable
                        ? t('order_builder.request_quote.ship_to_error')
                        : shipToName}
                  </span>
                </div>
              </div>

              <SelectLengthsStep
                lengths={lengths}
                priceUnit={priceUnit}
                currency={currency}
                selectedLengthIds={selectedLengthIds}
                onToggleLength={(id) =>
                  setSelectedLengthIds((previous) => toggleId(previous, id))
                }
                inputsByLengthId={inputsByLengthId}
                onUpdateInput={updateInput}
                touchedLengthIds={touchedLengthIds}
                onTouchLength={touchLength}
                nextAttempted={nextAttempted}
              />
            </div>
          )}

          {step === 'contacts' && (
            <div className="flex flex-col gap-3">
              <p className="text-sm text-neut-sec">
                {t('order_builder.request_quote.contacts_hint')}
              </p>
              {isContactDataError ? (
                <p className="text-sm text-error-600">
                  {t('order_builder.request_quote.contacts_error')}
                </p>
              ) : (
                <ul
                  className="rounded-lg border border-neut-border"
                  data-testid="request-quote-sellers"
                >
                  {organizations.map((organization) => (
                    <SellerContactsSummaryRow
                      key={organization.id}
                      organization={organization}
                      contacts={sellerContactsOf(
                        contactDataByOrgId,
                        organization.id
                      )}
                      loaded={contactDataLoaded}
                      selectedContactIds={selectedContactIdsFor(
                        organization.id
                      )}
                      primaryContactId={primaryContactIdByOrg.get(
                        organization.id
                      )}
                      primaryContactMode="optional"
                      onChange={updateSellerContacts}
                      labels={{
                        noContactsSelected: t(
                          'order_builder.request_quote.no_contacts_selected'
                        ),
                        noContacts: t(
                          'order_builder.request_quote.no_contacts'
                        ),
                      }}
                      testId={`request-quote-seller-${organization.id}`}
                      disabled={isPending}
                    />
                  ))}
                </ul>
              )}
            </div>
          )}

          {step === 'summary' && (
            <div
              className="flex h-full flex-col gap-3"
              data-testid="request-quote-summary"
            >
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-neut-sec">
                <span
                  className="flex items-center gap-2"
                  data-testid="request-quote-summary-needed-by"
                >
                  <Calendar aria-hidden="true" size={16} />
                  {t('order_builder.request_quote.needed_by_value', {
                    date: neededBy
                      ? formatDateOnly(neededBy)
                      : t('order_builder.request_quote.no_needed_by'),
                  })}
                </span>
                <span
                  className="flex items-center gap-2"
                  data-testid="request-quote-summary-ship-to"
                >
                  <MapPin aria-hidden="true" size={16} />
                  {shipToUnavailable
                    ? t('order_builder.request_quote.ship_to_error')
                    : t('order_builder.request_quote.ship_to_value', {
                        name: shipToName,
                      })}
                </span>
                <span data-testid="request-quote-summary-currency">
                  {t('order_builder.request_quote.currency_value', {
                    currency,
                  })}
                </span>
              </div>
              <div className="min-h-0 flex-1">
                <RequestForQuoteSummaryPanels
                  products={selectedLengths.map((item) => ({
                    id: item.length.platformProductLengthId,
                    name: productName,
                    lengthLabel: item.length.label,
                    quantityLabel: (
                      <BundleQuantityLabel
                        quantity={{
                          value: item.quantity,
                          unit: item.length.defaultQuantity.unit,
                        }}
                        piecesPerBundle={
                          item.length.platformProductsLength.platformProduct
                            .piecesPerBundle.value
                        }
                        platformProductsLength={
                          item.length.platformProductsLength
                        }
                      />
                    ),
                    priceLabel: t('order_builder.request_quote.prices_value', {
                      pickup: formatPrice(item.pickupPrice.amount),
                      delivery: formatPrice(item.deliveryPrice.amount),
                    }),
                  }))}
                  recipientGroups={recipientGroups}
                />
              </div>
            </div>
          )}
        </div>

        <div className="flex shrink-0 items-center justify-between gap-3 border-t border-neut-border bg-neut-50 px-8 py-4">
          <div>
            {step !== 'items' && (
              <Button
                variant="secondary"
                width="auto"
                data-testid="request-quote-back"
                disabled={isPending}
                onClick={() =>
                  setStep(step === 'summary' ? 'contacts' : 'items')
                }
              >
                {t('order_builder.request_quote.back')}
              </Button>
            )}
          </div>
          {step !== 'summary' && (
            <Button
              variant="primary"
              width="auto"
              data-testid="request-quote-next"
              aria-disabled={step === 'items' ? !itemsValid : !contactsValid}
              onClick={handleNext}
            >
              {t('order_builder.request_quote.next')}
            </Button>
          )}
          {step === 'summary' && (
            <Button
              variant="primary"
              width="auto"
              data-testid="request-quote-submit"
              loading={isPending}
              onClick={handleSubmit}
            >
              {t('order_builder.request_quote.submit')}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}
